import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useIncrementalAuth } from "@/hooks/useIncrementalAuth";
import { Loader2 } from "lucide-react";

interface ScopeRequestDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onSuccess?: () => void;
}

const ScopeRequestDialog = ({ isOpen, onClose, userId, onSuccess }: ScopeRequestDialogProps) => {
  const { triggerAuth, isPending } = useIncrementalAuth(userId);
  const [error, setError] = useState<string | null>(null);

  const handleGrantAccess = async () => {
    setError(null);
    try {
      await triggerAuth();
      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error("Failed to grant calendar access:", err);
      setError(err.message || "Failed to grant calendar access. Please try again.");
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isPending) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Calendar Access Required</DialogTitle>
          <DialogDescription>
            To use calendar features, we need permission to access your Google Calendar.
            This allows us to create and manage calendar events for your tasks.
          </DialogDescription>
        </DialogHeader>
        
        {error && (
          <div className="text-sm text-destructive bg-destructive/10 p-3 rounded">
            {error}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGrantAccess}
            disabled={isPending}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Granting Access...
              </>
            ) : (
              "Grant Calendar Access"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScopeRequestDialog;

